import { useState, useEffect } from "react";
import { Ticket, Download, RotateCcw, Ban, Plus, Loader2, RefreshCw } from "lucide-react";
import { api, euro, fmtDateTime, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS = {
  active: { label: "Aktiv", cls: "text-emerald-400 border-emerald-500/40" },
  redeemed: { label: "Eingelöst", cls: "text-neutral-400 border-white/20" },
  pending_payment: { label: "Zahlung offen", cls: "text-amber-400 border-amber-500/40" },
  cancelled: { label: "Storniert", cls: "text-[#ff0033] border-[#ff0033]/40" },
  expired: { label: "Abgelaufen", cls: "text-neutral-500 border-white/15" },
};

const VoucherManager = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: 50, recipient_name: "", recipient_email: "", message: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/vouchers"); setList(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const issue = async () => {
    if (!form.amount || Number(form.amount) < 1) { toast.error("Betrag angeben"); return; }
    setSaving(true);
    try {
      await api.post("/admin/vouchers", { ...form, amount: Number(form.amount) });
      toast.success("Gutschein ausgestellt");
      setShowForm(false); setForm({ amount: 50, recipient_name: "", recipient_email: "", message: "", note: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const resend = async (v) => {
    if (!v.email) { toast.error("Kein E-Mail-Empfänger hinterlegt"); return; }
    setBusy(v.id);
    try { await api.post(`/admin/vouchers/${v.id}/resend`); toast.success("Gutschein erneut versendet"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const doCancel = async () => {
    const v = cancelTarget; setCancelTarget(null); if (!v) return;
    setBusy(v.id);
    try { await api.post(`/admin/vouchers/${v.id}/cancel`); toast.success("Gutschein storniert"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const inputCls = "w-full bg-[#111] border border-white/15 text-white px-3 py-2.5 text-sm focus:border-[#ff0033] outline-none placeholder:text-neutral-600";

  return (
    <div data-testid="voucher-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white flex items-center gap-3"><Ticket size={22} className="text-[#ff0033]" /> Gutscheine</h2>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="voucher-refresh" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2.5 font-mono-lab text-xs uppercase tracking-widest disabled:opacity-50"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren</button>
          <button onClick={() => setShowForm(!showForm)} data-testid="voucher-new-btn" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest hover:bg-[#cc0029] transition-colors"><Plus size={14} /> Gutschein ausstellen</button>
        </div>
      </div>

      {showForm && (
        <div className="border border-white/10 bg-[#0b0b0b] p-4 mb-6 grid md:grid-cols-2 gap-3" data-testid="voucher-form">
          <label className="block"><span className="text-xs text-neutral-400">Betrag €</span><input data-testid="voucher-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} /></label>
          <label className="block"><span className="text-xs text-neutral-400">Empfänger-Name (optional)</span><input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className={inputCls} /></label>
          <label className="block"><span className="text-xs text-neutral-400">Empfänger-E-Mail (optional, sendet PDF)</span><input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className={inputCls} /></label>
          <label className="block"><span className="text-xs text-neutral-400">Interne Notiz (optional)</span><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} /></label>
          <label className="block md:col-span-2"><span className="text-xs text-neutral-400">Grußnachricht (auf dem PDF)</span><input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} /></label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="border border-white/15 text-neutral-300 px-4 py-2 font-mono-lab text-xs uppercase">Abbrechen</button>
            <button onClick={issue} disabled={saving} data-testid="voucher-issue-btn" className="bg-[#ff0033] text-white px-4 py-2 font-mono-lab text-xs uppercase flex items-center gap-2 disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : null} Ausstellen</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-neutral-500 py-10 text-center font-mono-lab text-sm">Lädt…</div>
        : list.length === 0 ? <div className="text-neutral-500 py-10 text-center font-mono-lab text-sm" data-testid="voucher-empty">Noch keine Gutscheine.</div>
        : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest">
                <th className="text-left p-3">Code</th><th className="text-left p-3">Wert / Guthaben</th><th className="text-left p-3">Status</th><th className="text-left p-3">Empfänger</th><th className="text-left p-3">Erstellt</th><th className="text-right p-3">Aktionen</th>
              </tr></thead>
              <tbody>
                {list.map((v) => {
                  const s = STATUS[v.status] || { label: v.status, cls: "text-neutral-400 border-white/20" };
                  return (
                    <tr key={v.id} className="border-b border-white/5 align-top" data-testid={`voucher-row-${v.id}`}>
                      <td className="p-3 font-mono-lab text-white whitespace-nowrap">{v.code}</td>
                      <td className="p-3 text-neutral-300">{euro(v.amount)} <span className="text-neutral-500">/ {euro(v.balance)}</span></td>
                      <td className="p-3"><span className={`inline-block font-mono-lab text-[10px] uppercase px-2 py-1 border ${s.cls}`}>{s.label}</span></td>
                      <td className="p-3 text-neutral-400">{v.recipient_name || "—"}{v.recipient_email ? <span className="block text-[11px] text-neutral-600">{v.recipient_email}</span> : null}</td>
                      <td className="p-3 text-neutral-500 whitespace-nowrap">{fmtDateTime(v.created_at, "de")}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {v.status !== "pending_payment" && <a href={`${API}/vouchers/${v.public_token}/pdf`} target="_blank" rel="noreferrer" data-testid={`voucher-pdf-${v.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-300 font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:text-white transition-colors"><Download size={12} /> PDF</a>}
                          {v.email && v.status !== "pending_payment" && <button onClick={() => resend(v)} disabled={busy === v.id} data-testid={`voucher-resend-${v.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-300 font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:text-white disabled:opacity-40 transition-colors"><RotateCcw size={12} /> Erneut senden</button>}
                          {v.status === "active" && <button onClick={() => setCancelTarget(v)} disabled={busy === v.id} data-testid={`voucher-cancel-${v.id}`} className="inline-flex items-center gap-1 border border-white/15 text-neutral-400 font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:text-[#ff0033] disabled:opacity-40 transition-colors"><Ban size={12} /> Stornieren</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="voucher-cancel-dialog">
          <AlertDialogHeader><AlertDialogTitle>Gutschein stornieren?</AlertDialogTitle>
            <AlertDialogDescription>{cancelTarget && `Der Gutschein ${cancelTarget.code} (${euro(cancelTarget.balance)} Restguthaben) wird ungültig und kann nicht mehr eingelöst werden.`}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={doCancel} data-testid="voucher-cancel-confirm" className="bg-[#ff0033] hover:bg-[#cc0029]">Stornieren</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VoucherManager;
